/** Thin fetch wrapper. Same-origin in dev (Vite proxy) and in production. */

export class ApiError extends Error {
  constructor(status, body) {
    super(body?.error?.message || `Erreur ${status}`);
    this.status = status;
    this.code = body?.error?.code ?? 'unknown';
    this.details = body?.error?.details;
  }
}

async function request(path, { method = 'GET', body, headers = {}, signal } = {}) {
  const res = await fetch(`/api${path}`, {
    method,
    headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...headers },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
  if (res.status === 204) return null;
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, payload);
  return payload;
}

/** Identity, not proof: says which participant this browser is acting as. */
const as = (memberId) => (memberId ? { 'X-Member-Id': memberId } : {});
const admin = (token) => (token ? { 'X-Admin-Token': token } : {});

export const api = {
  meta: () => request('/meta'),

  createTrip: (trip) => request('/trips', { method: 'POST', body: trip }),
  getTrip: (token, adminToken, signal) =>
    request(`/trips/${token}`, { headers: admin(adminToken), signal }),
  updateTrip: (token, adminToken, patch) =>
    request(`/trips/${token}`, { method: 'PATCH', headers: admin(adminToken), body: patch }),

  /** Claim an identity. An existing name resolves to that member, never a copy. */
  claimMember: (token, member) => request(`/trips/${token}/members`, { method: 'POST', body: member }),
  updateMember: (token, memberId, member, { as: me, adminToken } = {}) =>
    request(`/trips/${token}/members/${memberId}`, {
      method: 'PATCH',
      headers: { ...as(me), ...admin(adminToken) },
      body: member,
    }),

  createRide: (token, ride, { as: me } = {}) =>
    request(`/trips/${token}/rides`, { method: 'POST', headers: as(me), body: ride }),
  updateRide: (token, rideId, ride, { as: me, adminToken } = {}) =>
    request(`/trips/${token}/rides/${rideId}`, {
      method: 'PATCH',
      headers: { ...as(me), ...admin(adminToken) },
      body: ride,
    }),
  deleteRide: (token, rideId, { as: me, adminToken } = {}) =>
    request(`/trips/${token}/rides/${rideId}`, {
      method: 'DELETE',
      headers: { ...as(me), ...admin(adminToken) },
    }),

  /** Reshape a ride's road without resubmitting the rest of the ride. */
  setRideVia: (token, rideId, via, { as: me, adminToken } = {}) =>
    request(`/trips/${token}/rides/${rideId}/via`, {
      method: 'PUT',
      headers: { ...as(me), ...admin(adminToken) },
      body: { via },
    }),

  joinRide: (token, rideId, passenger, { as: me } = {}) =>
    request(`/trips/${token}/rides/${rideId}/passengers`, {
      method: 'POST',
      headers: as(me),
      body: passenger,
    }),
  updatePassenger: (token, rideId, passengerId, passenger, { as: me, adminToken } = {}) =>
    request(`/trips/${token}/rides/${rideId}/passengers/${passengerId}`, {
      method: 'PATCH',
      headers: { ...as(me), ...admin(adminToken) },
      body: passenger,
    }),
  leaveRide: (token, rideId, passengerId, { as: me, adminToken } = {}) =>
    request(`/trips/${token}/rides/${rideId}/passengers/${passengerId}`, {
      method: 'DELETE',
      headers: { ...as(me), ...admin(adminToken) },
    }),

  searchPlaces: (q, signal) => request(`/geo/search?q=${encodeURIComponent(q)}`, { signal }),
  reverse: (lat, lng, signal) => request(`/geo/reverse?lat=${lat}&lng=${lng}`, { signal }),
  previewRoute: (waypoints, signal) => request('/geo/route', { method: 'POST', body: { waypoints }, signal }),
};
